// Per-page engagement accumulator, emitting page_engaged.
//
// Tracks, for the current page, the time the tab was actually VISIBLE (paused
// while backgrounded) plus the max scroll depth reached, and emits a single
// `page_engaged` when the page is left (navigation / pagehide / unmount).
// Driven by usePageViews (lifecycle) + useScrollDepth (scroll). A module
// singleton so the App-level lifecycle and the GeneralLayout-level scroll
// listener share one current-page record.

import { track } from './index';

interface PageEngagement {
  page: string;
  visibleMs: number;
  lastVisibleAt: number | null; // timestamp since visible, or null while hidden
  maxScrollPct: number;
}

let current: PageEngagement | null = null;

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

/** Begin accounting for a page. Flushes the previous page's engagement first. */
export function startPage(page: string, nowMs: number = Date.now()): void {
  endPage(nowMs);
  current = { page, visibleMs: 0, lastVisibleAt: isVisible() ? nowMs : null, maxScrollPct: 0 };
}

/** Fold accrued visible time in and stop the clock (tab hidden). */
export function markHidden(nowMs: number = Date.now()): void {
  if (current && current.lastVisibleAt !== null) {
    current.visibleMs += nowMs - current.lastVisibleAt;
    current.lastVisibleAt = null;
  }
}

/** Restart the clock (tab visible again). */
export function markVisible(nowMs: number = Date.now()): void {
  if (current && current.lastVisibleAt === null) current.lastVisibleAt = nowMs;
}

/** Record a scroll-depth reading (percent); keeps the running max. */
export function recordScroll(pct: number): void {
  if (!current) return;
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  if (clamped > current.maxScrollPct) current.maxScrollPct = clamped;
}

/** Emit page_engaged for the current page (if any real visible time) and clear. */
export function endPage(nowMs: number = Date.now()): void {
  if (!current) return;
  markHidden(nowMs); // fold in trailing visible time
  const { page, visibleMs, maxScrollPct } = current;
  current = null;
  if (visibleMs > 0) {
    track('page_engaged', { engaged_ms: visibleMs, max_scroll_pct: maxScrollPct }, { page });
  }
}

/**
 * Test-only: drop the current record without emitting.
 * @internal
 */
export function __resetEngagement(): void {
  current = null;
}
